import React, { useState, useRef, useCallback } from 'react';
import { FiX, FiCamera, FiCheck, FiRotateCcw } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

function SimpleCameraCapture({ onChange, onClose }) {
  const [capturedImage, setCapturedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  
  const fileInputRef = useRef(null);

  // Función para manejar la captura de archivo
  const handleFileCapture = useCallback((event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setIsLoading(true);
      setErrorMessage(null);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setCapturedImage(e.target.result);
        setIsLoading(false);
      };
      
      reader.onerror = () => {
        setErrorMessage('Error al cargar la imagen');
        setIsLoading(false);
      };
      
      reader.readAsDataURL(file);
    } else if (file) {
      setErrorMessage('Por favor selecciona un archivo de imagen válido');
    }
  }, []);

  // Función para confirmar imagen
  const handleConfirm = useCallback(() => {
    if (capturedImage) {
      onChange(capturedImage);
      onClose();
    }
  }, [capturedImage, onChange, onClose]);

  // Función para retomar imagen
  const handleRetake = useCallback(() => {
    setCapturedImage(null);
    setIsLoading(false);
    setErrorMessage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, []);

  // Función para abrir la cámara
  const openCamera = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Header */}
      <div className="bg-black bg-opacity-90 text-white p-4 flex justify-between items-center">
        <h3 className="text-lg font-semibold">Capturar con Cámara</h3>
        <button 
          onClick={onClose} 
          className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
        >
          <SafeIcon icon={FiX} className="w-6 h-6" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        {isLoading ? (
          /* Estado de carga */
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-lg">Procesando imagen...</p>
          </div>
        ) : capturedImage ? (
          /* Vista previa de imagen capturada */
          <div className="w-full h-full flex flex-col items-center justify-center">
            <div className="relative max-w-full max-h-[70%] mb-4">
              <img 
                src={capturedImage} 
                alt="Imagen capturada" 
                className="max-w-full max-h-full object-contain rounded-lg shadow-lg" 
              />
            </div>
            <div className="text-white text-center mb-4">
              <p className="text-lg font-semibold mb-2">¿Usar esta imagen?</p>
              <p className="text-sm text-gray-300">Revisa que la firma sea visible</p>
            </div>
          </div>
        ) : (
          /* Estado inicial - solicitar captura */
          <div className="text-white text-center max-w-md">
            <SafeIcon icon={FiCamera} className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold mb-4">Captura de Imagen</h3>
            <p className="text-sm mb-6 text-gray-300">
              Toma una foto de la firma o selecciona una imagen existente
            </p>
            
            {errorMessage && (
              <div className="mb-6 p-4 bg-red-900 bg-opacity-30 border border-red-700 rounded-lg">
                <p className="text-sm text-red-200">{errorMessage}</p>
              </div>
            )}
            
            {/* Input oculto para captura de archivo */}
            <input 
              ref={fileInputRef}
              type="file" 
              accept="image/*" 
              capture="environment"
              onChange={handleFileCapture}
              className="hidden"
            />
            
            <button 
              onClick={openCamera}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
            >
              <SafeIcon icon={FiCamera} className="w-5 h-5" />
              Abrir Cámara
            </button>
            
            <div className="mt-6 p-4 bg-blue-900 bg-opacity-30 rounded-lg">
              <p className="text-xs text-blue-200">
                <strong>¿Cómo funciona?</strong>
              </p>
              <ol className="text-xs text-blue-300 space-y-1 text-left mt-2">
                <li>1. Presiona "Abrir Cámara"</li>
                <li>2. Tu dispositivo abrirá la app de cámara</li>
                <li>3. Toma la foto de la firma</li>
                <li>4. Selecciona la imagen que deseas usar</li>
              </ol>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="bg-black bg-opacity-90 p-6">
        <div className="flex justify-center items-center gap-6">
          {capturedImage ? (
            /* Controles para imagen capturada */
            <>
              <button 
                onClick={handleRetake}
                className="px-6 py-3 bg-white bg-opacity-20 text-white rounded-full hover:bg-opacity-30 transition-colors flex items-center gap-2"
              >
                <SafeIcon icon={FiRotateCcw} className="w-5 h-5" />
                Seleccionar otra
              </button>
              <button 
                onClick={handleConfirm}
                className="px-8 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors flex items-center gap-2"
              >
                <SafeIcon icon={FiCheck} className="w-5 h-5" />
                Confirmar imagen
              </button>
            </>
          ) : (
            /* Controles estado inicial */
            <button 
              onClick={onClose}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancelar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default SimpleCameraCapture;